import { useLayoutEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { create } from 'zustand';

/**
 * Page-chrome mechanism for the slim AppTopbar (Unified Activity Center, Part A).
 *
 * Two complementary, synchronous lookups drive the topbar's LEFT slot without
 * forcing any of the detail pages to add code:
 *  - ROUTE_BACK  — route → back affordance ({backTo, backLabel}) for detail
 *    pages. Works during loading/error (before DetailHeader mounts) because it's
 *    purely route-table driven.
 *  - ROUTE_TITLE — route → desktop section title for top-level pages.
 *
 * Pages that need a data-derived title (e.g. a ticket subject) publish richer
 * chrome at runtime via `usePageChromeStore` / `usePublishChrome` — DetailHeader
 * is the primary publisher (see DetailLayout.tsx).
 */

export interface PageChrome {
  /** Section / page title shown on the left when there's no back affordance. */
  title?: string;
  /** Where the back affordance navigates ("up" nav). */
  backTo?: string;
  /** Label shown next to the back glyph. */
  backLabel?: string;
  /** Optional: intercept the back affordance (e.g. to run a close View Transition)
   *  instead of plainly navigating to `backTo`. */
  onBack?: () => void;
}

/**
 * Detail routes → back affordance, mirroring App.tsx. Keyed by the react-router
 * path pattern so `matchRouteBack` can resolve a concrete pathname. The Knowledge
 * article reader + editor both back to the Articles list (the reader also
 * publishes a runtime close-transition via DetailHeader; see KnowledgeArticleReader).
 */
export const ROUTE_BACK: Record<string, { backTo: string; backLabel: string }> = {
  '/tickets/:id': { backTo: '/tickets', backLabel: 'Tickets' },
  '/backlog/:id': { backTo: '/backlog', backLabel: 'Backlog' },
  // Tenants (RealEquity platform sync) — the three specific child-entity patterns
  // MUST be tried before the generic single-segment `/tenants/:reId` in
  // ROUTE_BACK_ORDER, mirroring the Companies precedent below.
  '/tenants/rg/:reId': { backTo: '/tenants', backLabel: 'Tenants' },
  '/tenants/app/:reId': { backTo: '/tenants', backLabel: 'Tenants' },
  '/tenants/ds/:reId': { backTo: '/tenants', backLabel: 'Tenants' },
  '/tenants/:reId': { backTo: '/tenants', backLabel: 'Tenants' },
  // CRM (Companies) detail pages — the two-segment child patterns MUST be tried
  // before the single-segment `/companies/:id` in ROUTE_BACK_ORDER.
  '/companies/contacts/:id': { backTo: '/companies', backLabel: 'Companies' },
  '/companies/deals/:id': { backTo: '/companies', backLabel: 'Companies' },
  '/companies/leads/:id': { backTo: '/companies', backLabel: 'Companies' },
  '/companies/:id': { backTo: '/companies', backLabel: 'Companies' },
  '/backlog/:id/:slug': { backTo: '/backlog', backLabel: 'Backlog' }, // back-compat alias
  '/insights/employees/:id': { backTo: '/insights?tab=employees', backLabel: 'Employees' },
  '/commits/:id': { backTo: '/commits', backLabel: 'Tracker' },
  '/commits/analysis/:id': { backTo: '/commits', backLabel: 'Tracker' },
  '/support/:id': { backTo: '/support', backLabel: 'AI Support' },
  '/status/incidents/:id': { backTo: '/status', backLabel: 'Status Page' },
  '/topics/:id': { backTo: '/topics', backLabel: 'Topics' },
  // Release Planning keeps its tab in `?tab=` and DEFAULTS TO status, so a bare
  // `/releases` silently drops the tab you came from. Each sub-page names the tab
  // that opens it (b-01818) — same as /insights?tab=employees above.
  '/releases/:id': { backTo: '/releases?tab=prepare', backLabel: 'Releases' },
  '/releases/app/:app': { backTo: '/releases?tab=status', backLabel: 'Releases' },
  '/releases/tracking/:id': { backTo: '/releases?tab=tracking', backLabel: 'Releases' },
  // Two-segment editor pattern MUST be ordered before the single-segment reader
  // (:slug) so it isn't swallowed by it (see ROUTE_BACK_ORDER).
  '/knowledge/articles/:id/edit': { backTo: '/knowledge?tab=articles', backLabel: 'Articles' },
  '/knowledge/articles/:slug': { backTo: '/knowledge?tab=articles', backLabel: 'Articles' },
  '/solution-design/:id': { backTo: '/solution-design', backLabel: 'Solution Design' },
};

// More-specific patterns must be tried before catch-alls like `/releases/:id`
// (which would otherwise swallow `/releases/app/:app`). matchPath is exact, so
// `/releases/:id` won't match `/releases/app/foo` — but ordering keeps intent
// explicit and is robust if patterns are loosened later.
const ROUTE_BACK_ORDER: string[] = [
  '/tickets/:id',
  '/backlog/:id/:slug',
  '/backlog/:id',
  '/tenants/rg/:reId',
  '/tenants/app/:reId',
  '/tenants/ds/:reId',
  '/tenants/:reId',
  '/companies/contacts/:id',
  '/companies/deals/:id',
  '/companies/leads/:id',
  '/companies/:id',
  '/insights/employees/:id',
  '/commits/analysis/:id',
  '/commits/:id',
  '/support/:id',
  '/status/incidents/:id',
  '/topics/:id',
  '/releases/app/:app',
  '/releases/tracking/:id',
  '/releases/:id',
  '/knowledge/articles/:id/edit',
  '/knowledge/articles/:slug',
  '/solution-design/:id',
];

/**
 * Resolve a concrete pathname to its detail back affordance via react-router
 * matchPath. Returns undefined for top-level / unknown routes.
 */
export function matchRouteBack(pathname: string): { backTo: string; backLabel: string } | undefined {
  for (const pattern of ROUTE_BACK_ORDER) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      return ROUTE_BACK[pattern];
    }
  }
  return undefined;
}

/** Human label per detail-route kind (the first path segment). */
const ENTITY_KIND_LABEL: Record<string, string> = {
  tickets: 'Ticket', backlog: 'Backlog item', topics: 'Topic', commits: 'Commit',
  support: 'Support session', releases: 'Release', knowledge: 'Article', insights: 'Employee',
  companies: 'Company', tenants: 'Tenant', status: 'Incident',
  'solution-design': 'Specification',
};
export const entityKindLabel = (kind: string): string => ENTITY_KIND_LABEL[kind] ?? kind;

/**
 * Resolve a concrete pathname to the detail ENTITY it shows (kind + id), reusing
 * the same route patterns as the back affordance. Pathname-only, so it works from
 * anywhere (including the topbar request tray, outside the routed subtree). Returns
 * undefined for top-level / unknown routes. Used to capture a request's origin.
 */
export function matchRouteEntity(pathname: string): { kind: string; label: string; id: string } | undefined {
  for (const pattern of ROUTE_BACK_ORDER) {
    const m = matchPath({ path: pattern, end: true }, pathname);
    if (m) {
      const id = (m.params.id ?? m.params.reId ?? m.params.slug ?? m.params.app ?? '') as string;
      const segment = pattern.split('/')[1] ?? '';
      return { kind: segment, label: ENTITY_KIND_LABEL[segment] ?? segment, id };
    }
  }
  return undefined;
}

/**
 * Top-level route → desktop section title. Mirrors the Sidebar nav labels so the
 * topbar's left slot matches the active nav item. Synchronous; no data needed.
 */
export const ROUTE_TITLE: Record<string, string> = {
  '/': 'Dashboard',
  '/tenants': 'Tenants',
  '/tickets': 'Tickets',
  '/topics': 'Topics',
  '/backlog': 'Backlog',
  '/companies': 'Companies',
  '/inbox': 'Inbox',
  '/commits': 'Commit Tracker',
  '/releases': 'Release Planning',
  '/support': 'AI Support',
  '/status': 'Status Page',
  '/insights': 'Insights',
  '/knowledge': 'Knowledge Base',
  '/solution-design': 'Solution Design',
  '/requests': 'Requests',
  '/playbooks': 'Playbooks',
  '/entitlements': 'Entitlements',
  '/coco-config': 'CoCo Config',
  '/activity': 'Activity',
  '/advanced': 'Advanced',
  '/settings': 'Settings',
  '/search': 'Search',
  '/profile': 'Profile',
  '/integrations': 'Integrations',
  '/chat': 'Chat',
  '/design': 'Design Reference',
};

/** Synchronous section title for a top-level page (undefined if not mapped). */
export function matchRouteTitle(pathname: string): string | undefined {
  const exact = ROUTE_TITLE[pathname];
  if (exact) return exact;
  // Knowledge article editor (/knowledge/articles/:id) shares the KB title.
  if (matchPath({ path: '/knowledge/articles/:id', end: true }, pathname)) {
    return ROUTE_TITLE['/knowledge'];
  }
  return undefined;
}

// ─── Chrome store (runtime publisher; NOT persisted) ───────────────

interface PageChromeState extends PageChrome {
  setPageChrome: (chrome: PageChrome) => void;
  resetPageChrome: () => void;
}

const EMPTY: PageChrome = { title: undefined, backTo: undefined, backLabel: undefined, onBack: undefined };

export const usePageChromeStore = create<PageChromeState>()((set) => ({
  ...EMPTY,
  setPageChrome: (chrome) =>
    set({ title: chrome.title, backTo: chrome.backTo, backLabel: chrome.backLabel, onBack: chrome.onBack }),
  resetPageChrome: () => set({ ...EMPTY }),
}));

/**
 * Publish page chrome for the lifetime of the calling component: sets on mount /
 * whenever the chrome changes, and resets on unmount. Layout-effect timing so the
 * topbar updates in the same paint as the page swaps (no flash of stale chrome).
 */
export function usePublishChrome(chrome: PageChrome): void {
  const { title, backTo, backLabel, onBack } = chrome;
  useLayoutEffect(() => {
    usePageChromeStore.getState().setPageChrome({ title, backTo, backLabel, onBack });
    return () => usePageChromeStore.getState().resetPageChrome();
  }, [title, backTo, backLabel, onBack]);
}
